<template>
    <div class="font-body">
        <LandingNav
            background-color="#FFFFFF"
            logo-light="/hkcm/images/logo-on-light-background.svg"
            logo-small-light="/hkcm/images/logo-small-on-light-background.svg"
            :cta-text="'Analyse-Pakete entdecken'"
            cta-link="/produkte"
            button-gradient-from="#006CFF"
            button-gradient-to="#006CFF"
            :show-ticker="true"
            ticker-text="+++ Ø<span class='font-semibold'> 70 % Trefferquote</span> über alle Analysebereiche +++ <span class='font-semibold'>Klare</span> Einstiegs-, Ausstiegs- und Risikomarken +++ <span class='font-semibold'>Strukturierte Szenarien</span> statt Bauchgefühl +++"
        />

        <!-- HERO -->
        <HeroSection
            background-color="radial-gradient(circle at 50% 50%, #072551 0%, #051A39 50%, #020E20 100%)"
            :badges="heroBadges"
            title="Erfolgreich in Indizes investieren mit technischer Chartanalyse der HKCM"
            title-highlight="Indizes"
            title-highlight-gradient-from="#F68242"
            title-highlight-gradient-to="#9D4A1E"
            subtitle="Strukturierte Analysen führender Aktienindizes schriftlich oder als Video. Chartbilder mit<br class='hidden lg:block'>klaren Einstiegsbereichen und Zielzonen auf Basis der Elliott-Wellen-Analyse."
            :benefits="heroBenefits"
            checkmark-color="text-[#006CFF]"
            cta-text="Analyse-Pakete abonnieren"
            cta-link="/produkte"
            price-color="text-orange"
            :social-proof-text="'<div>Bereits über 25.000</div><div>zufriedene Kunden</div>'"
            social-proof-image="/hkcm/images/rezensionenBilder.png"
            :app-store-data="appStoreData"
            :google-play-data="googlePlayData"
            background-image="/hkcm/images/bg_candels.png"
            button-gradient-from="#F68242"
            button-gradient-to="#9D4A1E"
        />

        <!-- VIDEO SECTION -->
        <VideoSection
            background-color="transparent"
            video-url="https://iframe.mediadelivery.net/embed/369702/2f5b2ca6-5673-4be1-9531-e645339f6402?autoplay=false&loop=false&muted=false&preload=true&responsive=true"
            :video-height="500"
        />

        <!-- WHY UNSYSTEMATIC DECISIONS -->
        <ContentSection
            background-color="#FFFFFF"
            title="Warum viele Indexentscheidungen unsystematisch getroffen werden"
            title-highlight="Indexentscheidungen"
            title-highlight-gradient-from="#F68242"
            title-highlight-gradient-to="#9D4A1E"
            title-color="text-blue-dark"
            :paragraphs="whyUnsystematicParagraphs"
            cta-text="Analyse-Pakete abonnieren"
            cta-link="/produkte"
            button-gradient-from="#F68242"
            button-gradient-to="#9D4A1E"
            :tiles="statsCards"
            :tiles-background="'bg-[#e5f0ff]'"
            :tiles-title-color="'text-[#006CFF]'"
            :tiles-description-color="'text-black'"
        />

        <!-- WHAT YOU GET IN INDEX PACKAGES -->
        <FeatureCardsSection
            background-color="bg-gradient-to-b from-[#072551] to-[#020E20]"
            title="Das erhalten Sie in<br> unseren Index-Paketen"
            title-highlight="Index-Paketen"
            title-highlight-gradient-from="#F68242"
            title-highlight-gradient-to="#9D4A1E"
            title-color="text-white"
            :cards="featureCards"
            card-background-color="bg-white"
            card-title-color="text-blue-dark"
            card-text-color="text-black"
            card-icon-color="text-blue"
            icon-size="text-[120px]"
            card-width="lg:w-[28rem] w-80"
            cta-text="Analyse-Pakete jetzt kaufen"
            cta-link="/produkte"
            button-gradient-from="#F68242"
            button-gradient-to="#9D4A1E"
        />

        <!-- INDICES PACKAGES SECTION -->
        <AnalysePaketeSection
            background-color="linear-gradient(135deg, #F68242 0%, #9D4A1E 100%)"
            title="Diese Indizes erhalten Sie<br> bei der HKCM"
            title-highlight="Indizes"
            title-highlight-color="text-white"
            title-color="text-black"
            subtitle="Wählen Sie das Index-Paket passend zu ihrem Marktinteresse und Ihrer Strategie"
            subtitle-color="text-black"
            :packages="indicesPakete"
            button-gradient-from="#F68242"
            button-gradient-to="#9D4A1E"
            icon-color="text-black"
            icon-image="https://hkcm-storage.s3.eu-central-1.amazonaws.com/product-categories/indizes-white.png"
        />

        <!-- WHAT ARE INDICES -->
        <VideoContentSection
            background-color="bg-white"
            title="Was sind Aktienindizes<br class='hidden lg:block'> und warum sind sie wichtig?"
            title-highlight="Aktienindizes"
            title-highlight-gradient-from="#F68242"
            title-highlight-gradient-to="#9D4A1E"
            title-color="text-blue-dark"
            content="Ein Aktienindex fasst die Kursentwicklung mehrerer Unternehmen in einer Kennzahl zusammen. Er zeigt die übergeordnete Marktbewegung, unabhängig von einzelnen Aktien.<br><br><strong>Beispiele:</strong><br>• DAX → 40 größte börsennotierte Unternehmen in Deutschland<br>• S&P 500 → breite US-Marktabbildung<br>• Nasdaq → technologieorientierte Unternehmen<br><br><strong>Indizes helfen dabei:</strong><br>• Markttrends zu erkennen<br>• Gesamtmarkt-Risiken einzuschätzen<br>• Überhitzungen oder Korrekturphasen zu identifizieren<br><br>Unsere Analysen setzen genau hier an, bei der Struktur des Gesamtmarktes."
            text-color="text-black"
            :height="500"
            position="right"
            button-gradient-from="#F68242"
            button-gradient-to="#9D4A1E"
            :image="$asset('/hkcm/images/methodology2.png', 'public')"
        />

        <!-- ELLIOTT WAVES ANALYSIS -->
        <VideoContentSection
            background-color="bg-gradient-to-b from-[#072551] to-[#020E20]"
            title="Wie wir Indexmärkte mit Elliott-Wellen analysieren"
            title-highlight="Indexmärkte"
            title-highlight-gradient-from="#f68242"
            title-highlight-gradient-to="#9D4A1E"
            title-color="text-white"
            content="Indexmärkte bewegen sich häufig in wiederkehrenden Strukturmustern. Die Elliott-Wellen-Methodik beschreibt diese Bewegungszyklen systematisch.<br><br>Wir analysieren:<br>• Trendwellen<br>• Korrekturphasen<br>• Strukturbrüche<br>• Zielzonen<br><br>Daraus entstehen Szenarien und Kursmarken."
            text-color="text-white"
            video-url="https://iframe.mediadelivery.net/embed/369702/6778efa1-0acd-4861-8552-483f2c43bf97?autoplay=false&loop=false&muted=false&preload=true&responsive=true"
            :height="384"
            position="left"
            button-gradient-from="#F68242"
            button-gradient-to="#9D4A1E"
        />

        <!-- 16) WHAT YOU GET SECTION -->
        <WhatYouGetSection
            title="Was Sie bei HKCM konkret erhalten und was nicht"
            title-highlight="HKCM"
            title-highlight-gradient-from="#F68242"
            title-highlight-gradient-to="#9D4A1E"
            title-color="#ffffff"
            description="Damit keine falschen Erwartungen entstehen, ist uns Transparenz wichtig:<br> HKCM ist kein Broker, keine Handelsplattform und keine Vermögensverwaltung. Sie kaufen bei uns keine Aktien und führen über uns keine Transaktionen aus. Stattdessen erhalten Sie strukturierte, methodisch abgeleitete Marktanalysen auf Basis der Elliott-Wellen-Theorie. Als fundierte Entscheidungsgrundlage für Ihre eigenen Investmententscheidungen."
            description-color="#cbd5e1"
            cta-text="Analyse-Pakete auswählen"
            cta-link="/produkte"
            cta-style="lg:px-4 py-4 mt-6 rounded-full font-bold text-[20px] flex items-center gap-2 transition text-white hover:opacity-90 w-full justify-center lg:w-fit bg-gradient-to-r from-[#f68242] to-[#9d4a1e]"
            :image="$asset('/hkcm/images/frau_lp.png', 'public')"
            image-alt="HKCM Experte"
            :show-cta="true"
        />

        <!-- WHO IS IT FOR -->
        <WhoIsItForSection
            title="Für wen unsere Analysen geeignet sind"
            title-highlight="Analysen"
            title-highlight-gradient-from="#F68242"
            title-highlight-gradient-to="#9D4A1E"
            title-color="#1a1a1a"
            intro-text="Unsere Index-Pakete richten sich an Anleger, die ihre Investmententscheidungen eigenständig treffen, dabei aber nicht auf Bauchgefühl oder Schlagzeilen vertrauen möchten.<br> Die Analysen liefern Ihnen strukturierte Marktmodelle, Szenarien und Kursmarken.<br> Sie sind als Entscheidungsgrundlage gedacht, nicht als automatisches Handelssystem.<br> Damit Sie schnell erkennen können, ob unser Ansatz zu Ihnen passt, finden Sie hier eine klare Einordnung."
            intro-text-color="#000000"
            subtitle="Besonders geeignet sind die Analysen für Sie, wenn …"
            subtitle-highlight="Analysen"
            :cards="whoIsItForCards"
            card-border-color="transparent"
            card-title-color="#1a1a1a"
            card-text-color="#000000"
            card-background="bg-[#e5f0ff]"
        />

        <!-- IMPORTANT TO UNDERSTAND -->
        <VideoContentSection
            background-color="bg-gradient-to-b from-[#072551] to-[#020E20]"
            title="Wichtig zu verstehen"
            title-highlight="verstehen"
            title-highlight-gradient-from="#F68242"
            title-highlight-gradient-to="#9D4A1E"
            title-color="text-white"
            content="Unsere Analysen sind ein Werkzeug für strukturierte Entscheidungen, kein Ersatz für eigene Verantwortung. Je aktiver und strukturierter Sie investieren, desto größer ist der praktische Nutzen der Analysen."
            text-color="text-white"
            video-url="https://iframe.mediadelivery.net/embed/369702/ecd8161c-9187-4656-ad4d-afabe1ca5c76?autoplay=false&loop=false&muted=false&preload=true&responsive=true"
            :height="384"
            position="left"
            cta-text="Analyse-Pakete auswählen"
            cta-link="/produkte"
            button-gradient-from="#F68242"
            button-gradient-to="#9D4A1E"
        />

        <!-- HOW TO USE SECTION -->
        <HowToUseSection
            background-color="#ffffff"
            title="So nutzen Sie unsere Analysen"
            title-highlight="Analysen"
            title-highlight-gradient-from="#F68242"
            title-highlight-gradient-to="#9D4A1E"
            title-color="#1a1a1a"
            :steps="howToUseSteps"
            disclaimer="HKCM bietet Marktanalysen und Szenarien auf Basis der Elliott-Wellen-Theorie. Es erfolgt keine Anlageberatung und keine Kaufabwicklung."
            disclaimer-color="#000000"
            active-step-color="#006CFF"
            inactive-step-color="#1e3a5f"
            line-color="#1e3a5f"
            label-color="#1a1a1a"
        />

        <!-- WHO IS BEHIND HKCM -->
        <WhoIsBehindSection
            background-color="bg-[#006cff1a]"
            title="Wer hinter HKCM steht"
            title-highlight="HKCM"
            title-highlight-gradient-from="#F68242"
            title-highlight-gradient-to="#9D4A1E"
            title-color="#1a1a1a"
            intro-text="Strukturierte Marktanalyse mit klarer Methodik. Transparent, nachvollziehbar und regelmäßig aktualisiert."
            intro-text-color="#000000"
            :paragraphs="whoIsBehindParagraphs"
            paragraph-color="#000000"
            image="/hkcm/images/team/pk.jpg"
            image-alt="Philip Klinkmüller"
            badge-top-text="Über"
            badge-main-text="70 %"
            badge-bottom-text="Trefferquote"
            :badge-size-type="1"
            :features="whoIsBehindFeatures"
            feature-title-color="text-[#006CFF]"
            feature-subtitle-color="#000000"
            checkmark-color="text-[#006CFF]"
            :multiple-buttons="[]"
        />

        <!-- FINAL CTA -->
        <FinalCTASection
            background-color="linear-gradient(to bottom, #072551, #020E20)"
            title="Finden Sie jetzt das passende Aktienanalyse-Paket für Ihre Strategie"
            title-highlight="Aktienanalyse-Paket"
            title-highlight-gradient-from="#F68242"
            title-highlight-gradient-to="#9D4A1E"
            title-color="text-white"
            description="Alle Pakete basieren auf methodischer Marktanalyse statt auf Prognose. Transparent und strukturiert abgeleitet."
            description-color="text-white"
            image="/hkcm/images/zugang_zum_hype_aktien_paket.png"
            image-alt="Index Analyse"
            image-position="left"
            cta-text="Analyse-Pakete auswählen"
            cta-link="/produkte"
            button-gradient-from="#F68242"
            button-gradient-to="#9D4A1E"
            price-color="text-orange"
        />

        <!-- FOOTER -->
        <LandingFooter
            background-color="#FFFFFF"
            logo="/hkcm/images/logo-on-light-background.svg"
            logo-link="/produkte"
            link-color="text-[#031837]"
            cta-text="Analyse-Pakete entdecken"
            cta-link="/produkte"
            button-gradient-from="#006CFF"
            button-gradient-to="#006CFF"
        />
    </div>
</template>

<script setup>
import i18next from 'i18next';
import LandingNav from '../components/landing/LandingNav.vue';
import HeroSection from '../components/landing/HeroSection.vue';
import VideoSection from '../components/landing/VideoSection.vue';
import ContentSection from '../components/landing/ContentSection.vue';
import FeatureCardsSection from '../components/landing/FeatureCardsSection.vue';
import VideoContentSection from '../components/landing/VideoContentSection.vue';
import AnalysePaketeSection from '../components/landing/AnalysePaketeSection.vue';
import HowToUseSection from '../components/landing/HowToUseSection.vue';
import WhoIsItForSection from '../components/landing/WhoIsItForSection.vue';
import WhoIsBehindSection from '../components/landing/WhoIsBehindSection.vue';
import LandingFooter from '../components/landing/LandingFooter.vue';
import FinalCTASection from '../components/landing/FinalCTASection.vue';
import WhatYouGetSection from '../components/landing/WhatYouGetSection.vue';

// App Store data
const appStoreData = {
    link: 'https://apps.apple.com/de/app/hkcm/id6499010943',
    badge: i18next.language === 'de' ? '/hkcm/images/apple-store-badge.png' : '/hkcm/images/apple-store-badge-en.png',
    rating: '4.4',
    stars: '/hkcm/images/stars4_5_yellow.png',
    reviews: '1.8k'
};

// Google Play data
const googlePlayData = {
    link: 'https://play.google.com/store/apps/details?id=de.hkcm.app&hl=de&pli=1',
    badge: i18next.language === 'de' ? '/hkcm/images/google-play-badge.png' : '/hkcm/images/google-play-badge-en.png',
    rating: '4.5',
    stars: '/hkcm/images/stars4_5_green.png',
    reviews: '7.3k'
};

// Hero badges - These are from the Figma design
const heroBadges = [
    { src: '/hkcm/images/partners/BlackBull24_Krypto_square.png', alt: 'Black Bull Award Winner' },
    { src: '/hkcm/images/partners/BlackBull25_InvestmentVision.png', alt: 'Investment Visionary Award' },
    { src: '/hkcm/images/partners/DISQTrust_Siegel_Von Kunden_Empfohlen_HKCM.png', alt: 'DISQ Trust', class: 'w-full object-contain max-h-[180px] lg:w-auto' }
];

// Hero benefits
const heroBenefits = [
    'Chartanalyse mit<br class="lg:block hidden"> Einstiegsbereichen und<br class="lg:block hidden"> Zielzonen',
    'Regelmäßige<br class="lg:block hidden"> Updates<br class="lg:block hidden"> der Tagescharts',
    'Methodik<br class="lg:block hidden"> statt Meinungs-<br class="lg:block hidden">Newsletter',
    'Führende Indizes wie<br class="lg:block hidden"> DAX, Nasdaq100, S&P<br class="lg:block hidden"> 500, Hang Seng'
];

// Why Unsystematic paragraphs
const whyUnsystematicParagraphs =
    'Viele Index-Anleger beobachten Indizes wie DAX, Nasdaq, Hang Seng oder S&P 500 und treffen ihre Entscheidungen auf Basis von Nachrichten, Einzelmeinungen oder kurzfristigen Trends.<br><br>' +
    '<strong>Typische Folgen:</strong><br><br>' +
    '<ul style="list-style-type:disc; margin-left: 30px; margin-bottom: 10px;" class="text-black">' +
    '<li class="text-sm"><span class="text-[18px]">verspätete Reaktionen</span></li>' +
    '<li class="text-sm"><span class="text-[18px]">falsche Trendinterpretationen</span></li>' +
    '<li class="text-sm"><span class="text-[18px]">fehlende Risikomarken</span></li>' +
    '<li class="text-sm"><span class="text-[18px]">keine Szenario-Planung</span></li>' +
    '</ul>' +
    'Unsere Indexanalysen liefern strukturierte Chartbilder mit konkreten Zielzonen und Einstiegsbereichen. Methodisch abgeleitet statt emotional bewertet.';

// Stats Cards
const statsCards = [
    { title: '+15', description: 'Jahre Erfahrung am <br> Markt' },
    { title: '+15.000', description: 'Analysen<br>erstellt' },
    { title: '+1 mio.', description: 'Follower auf<br>YouTube' },
    { title: '+290', description: 'Durchschnittlich <br> Analysen im Monat' }
];

// Feature Cards
const featureCards = [
    {
        title: 'Klare<br> Handelsmöglichkeiten',
        icon: '/hkcm/images/landing-icons/crosshair.svg',
        content:
            'Einstiegs- und Ausstiegszonen auf Basis der Elliott-Wellen-Analyse zum einfachen Nachhandeln: per Mail, direkt in der App (inklusive Push-Benachrichtigung) oder jederzeit im HKCM-Dashboard.'
    },
    {
        title: 'Zugang zum<br>  Musterportfolio',
        icon: '/hkcm/images/landing-icons/briefcase.svg',
        content: 'Unser Musterportfolio macht unsere Performance und Handelsentscheidungen jederzeit transparent nachvollziehbar.'
    },
    {
        title: 'Tägliche<br>  Analyse',
        icon: '/hkcm/images/landing-icons/chart-line.svg',
        content: 'In detaillierten Indexanalysen erhalten Sie werktägliche schriftliche Updates der bedeutendsten Indizes aus den wichtigsten Regionen der Welt.'
    },
    {
        title: 'Zubuchbare<br>  Videoanalysen',
        icon: '/hkcm/images/landing-icons/video-camera.svg',
        content:
            'Zweimal pro Woche, dienstags und donnerstags, haben Sie die Möglichkeit, Videoanalysen zu den enthaltenen Indizen hinzuzubuchen. Diese bieten Ihnen eine sinnvolle Erweiterung des regulären Abonnements und unterstützen Sie dabei, Marktbewegungen noch besser nachzuvollziehen und einzuordnen.'
    },
    {
        title: 'Schnelle Reaktionen auf<br>  Marktbewegungen',
        icon: '/hkcm/images/landing-icons/lightning.svg',
        content: 'Bei starken Kursbewegungen oder wichtigen Ereignissen erhalten Sie kurze Updates, direkt als Kurznachricht in Ihrem Dashboard oder in der HKCM-App.'
    },
    {
        title: 'Übersichtliche<br>  Chartbilder',
        icon: '/hkcm/images/landing-icons/chart-bar.svg',
        content:
            'Mit klar markierten Unterstützungen, Widerständen und Zielzonen geben Ihnen die Chartbilder eine wertvolle Orientierung, sowohl auf der Long- als auch auf der Short-Seite.'
    }
];

// Indices Packages - Based on Figma design
const indicesPakete = [
    {
        name: 'Nasdaq',
        image: 'https://hkcm-storage.s3.eu-central-1.amazonaws.com/products/nasdaq.png',
        link: '/produkte/nasdaq',
        buttonText: 'Auswählen',
        infoText: 'Nasdaq 100 Index Analysen'
    },
    {
        name: 'Hang Seng',
        image: 'https://hkcm-storage.s3.eu-central-1.amazonaws.com/products/hang-seng.png',
        link: '/produkte/hang-seng',
        buttonText: 'Auswählen',
        infoText: 'Hang Seng Index Analysen'
    },
    {
        name: 'DAX/MDAX',
        image: 'https://hkcm-storage.s3.eu-central-1.amazonaws.com/products/dax.png',
        link: '/produkte/dax',
        buttonText: 'Auswählen',
        infoText: 'DAX und MDAX Index Analysen'
    },
    {
        name: 'S&P 500',
        image: 'https://hkcm-storage.s3.eu-central-1.amazonaws.com/products/sup-500.png',
        link: '/produkte/sp500',
        buttonText: 'Auswählen',
        infoText: 'S&P 500 Index Analysen'
    },
    {
        name: 'Dow Jones/Russell 2000',
        image: 'https://hkcm-storage.s3.eu-central-1.amazonaws.com/products/dowjones.png',
        link: '/produkte/dow-jones',
        buttonText: 'Auswählen',
        infoText: 'Dow Jones und Russell 2000 Index Analysen'
    }
];

// How to Use Steps
const howToUseSteps = [
    {
        label: 'Analysepaket\nauswählen',
        active: true,
        iconImage: '/hkcm/images/landing-icons/package.svg'
    },
    {
        label: 'Zugriff\nerhalten',
        active: false,
        iconImage: '/hkcm/images/landing-icons/key.svg'
    },
    {
        label: 'Kursmarken &\nSzenarien einsehen',
        active: false,
        iconImage: '/hkcm/images/landing-icons/chart-line-step.svg'
    },
    {
        label: 'Eigene Entscheidungen\nableiten',
        active: false,
        iconImage: '/hkcm/images/landing-icons/lightbulb-step.svg'
    }
];

// Who Is It For Cards
const whoIsItForCards = [
    {
        title: '… Sie Ihre Investmententscheidungen selbst treffen',
        description:
            'Sie möchten nicht blind Empfehlungen folgen, sondern nachvollziehen, warum bestimmte Marktbewegungen entstehen können. Unsere Analysen zeigen Struktur, Szenarien und Entscheidungsmarken. Die Umsetzung liegt bei Ihnen.',
        icon: '/hkcm/images/landing-icons/brain.svg'
    },
    {
        title: '… Sie mit Kursmarken und Szenarien arbeiten möchten',
        description:
            'Unsere Methodik liefert keine pauschalen „Kaufen / Verkaufen"-Aussagen. Stattdessen erhalten Sie konkrete Marktmarken und alternative Szenarien. Das ist ideal für Anleger, die strukturiert planen und Entscheidungen an klaren Bedingungen ausrichten möchten.',
        icon: '/hkcm/images/landing-icons/chart-line-up.svg'
    },
    {
        title: '… Sie regelmäßig Kapital in Aktien investieren',
        description:
            'Die Analysepakete entfalten ihren größten Nutzen für Anleger, die fortlaufend investieren oder bestehende Positionen aktiv managen. Wenn Sie monatlich oder regelmäßig Kapital am Markt einsetzen, helfen strukturierte Marktanalysen besonders bei Timing- und Risikofragen.',
        icon: '/hkcm/images/landing-icons/coins.svg'
    },
    {
        title: '… Sie Marktbewegungen besser verstehen wollen',
        description:
            'Viele Nutzer schätzen unsere Analysen nicht nur wegen der Marken — sondern wegen des Marktverständnisses. Sie lernen zu erkennen: in welcher Marktphase sich ein Index befindet, ob eine Bewegung strukturell stabil wirkt, wo Korrekturen typisch sind, wann Strukturen brechen. Das stärkt Ihre eigene Entscheidungskompetenz.',
        icon: '/hkcm/images/landing-icons/lightbulb.svg'
    }
];

// Who Is Behind Section - Paragraphs
const whoIsBehindParagraphs = [
    'HKCM steht für strukturierte Marktanalyse statt Meinungsprognose. Unser Fokus liegt darauf, Marktbewegungen methodisch einzuordnen und daraus klare Szenarien sowie Kursmarken abzuleiten.',
    'Unsere Analysen basieren auf der Elliott-Wellen-Theorie. Nicht, weil sie „kompliziert klingt", sondern weil sie Marktphasen und Korrekturen systematisch beschreibbar macht. Das Ziel ist nicht, den Markt perfekt vorherzusagen, sondern Ihnen ein nachvollziehbares Modell zu geben, mit dem Sie Entscheidungen strukturierter treffen können.',
    'Wir arbeiten mit einem Szenario basierten Ansatz. Das bedeutet: Sie erhalten nicht nur eine Richtung, sondern mehrere mögliche Entwicklungen, inklusive klarer Marken, ab wann ein Szenario bestätigt oder verworfen ist. So entsteht Orientierung, gerade in volatilen Marktphasen.'
];

// Who Is Behind Section - Features
const whoIsBehindFeatures = [
    {
        title: "Methodik statt<br class='hidden lg:block'> Meinung",
        subtitle: 'feste<br>Analyse-Logik'
    },
    {
        title: "Szenarien statt<br class='hidden lg:block'> Prognosen",
        subtitle: 'klar definierte<br> Bedingungen'
    },
    {
        title: "Kursmarken statt<br class='hidden lg:block'> Bauchgefühl",
        subtitle: 'transparente Entscheidungszonen'
    },
    {
        title: 'Regelmäßige<br class="hidden lg:block"> Updates',
        subtitle: 'Anpassungen bei Marktveränderungen'
    }
];
</script>

<style scoped></style>
